
import { motion } from 'motion/react';
import { Link } from 'react-router';
import { SEO } from './SEO';
import { StructuredData, getBreadcrumbSchema } from './StructuredData';
import {
  Eye,
  Target,
  Heart,
  Shield,
  Users,
  Car,
  Truck,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Clock,
  Wrench,
  MapPin,
  Award,
  Handshake,
  TrendingUp,
  Smile,
  Flame,
} from 'lucide-react';

/* ───── reusable fade-in wrapper ───── */
function FadeIn({
  children,
  className = '',
  delay = 0,
  direction = 'up',
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'left' | 'right' | 'none';
}) {
  const variants = {
    up: { hidden: { opacity: 0, y: 60 }, visible: { opacity: 1, y: 0 } },
    left: { hidden: { opacity: 0, x: -60 }, visible: { opacity: 1, x: 0 } },
    right: { hidden: { opacity: 0, x: 60 }, visible: { opacity: 1, x: 0 } },
    none: { hidden: { opacity: 0 }, visible: { opacity: 1 } },
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, delay, ease: 'easeOut' }}
      variants={variants[direction]}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ───── fleet data ───── */
const fleetItems = [
  { seats: '14-Seater', icon: Truck, desc: 'Comfortable mid-size shuttle for executive teams.' },
  { seats: '8-Seater', icon: Users, desc: 'Luxury van ideal for small private groups.' },
  { seats: '4-Seater', icon: Car, desc: 'Executive sedan for VIP transfers and personal travel.' },
];

/* ───── values data ───── */
const values = [
  { label: 'Honesty', icon: Award, color: 'from-gold-base/20 to-gold-base/5' },
  { label: 'Respect for Those Around You', icon: Handshake, color: 'from-emerald-500/20 to-emerald-500/5' },
  { label: 'Transmit Ethical Behaviour', icon: Heart, color: 'from-rose-500/20 to-rose-500/5' },
  { label: 'Positive Attitude', icon: Smile, color: 'from-sky-500/20 to-sky-500/5' },
  { label: 'Passion for Success', icon: Flame, color: 'from-amber-500/20 to-amber-500/5' },
];

/* ───── vehicle ops data ───── */
const vehicleOps = [
  'Passenger liability cover of R500 000 (Five Hundred Thousand) per vehicle',
  'Monitoring of vehicle movements',
  'Preventative maintenance schedules for all vehicles',
  'Service maintenance schedules',
  'Daily operational feedback meetings',
  'We operate country-wide',
];

/* ───── internal procedures data ───── */
const procedures = [
  'Daily, weekly operational planning meetings',
  'Operational procedures to ensure a quality service at all times',
  'Daily management meetings',
  'Planning of service schedules for maintenance on trucks and trailers',
  'Mechanics on 24 hour standby for breakdowns',
];

/* ═══════════════════════════════════════════
   ABOUT PAGE
   ═══════════════════════════════════════════ */
export function About() {
  return (
    <div className="flex flex-col min-h-screen bg-primary overflow-hidden">
      <SEO 
        title="About Skiva Africa Tours | Transport Company Johannesburg"
        description="Learn about Skiva Africa Tours, a professional South African transport and tourism company providing private, corporate, group and tourism transportation solutions."
        canonical="https://skivatours.com/about"
      />
      <StructuredData 
        data={getBreadcrumbSchema([
          { name: 'Home', url: 'https://skivatours.com/' },
          { name: 'About', url: 'https://skivatours.com/about' }
        ])} 
      />

      {/* ─── 1. HERO SECTION ─── */}
      <section className="relative h-[70vh] md:h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/about-hero-safari.jpg"
            alt="Skiva Africa Tours vehicle on a safari trail through the African bush"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-primary" />
        </div>

        <div className="container mx-auto px-4 z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <p className="text-gold-base text-sm md:text-base font-bold tracking-[0.4em] uppercase mb-6">
              Discover Our Story
            </p>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white uppercase tracking-tighter leading-tight">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light to-gold-base">Us</span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl mx-auto font-light tracking-wide"
          >
            Elevating the standard of passenger transport in South Africa — from merely functional to truly exceptional.
          </motion.p>
        </div>

        {/* decorative scroll indicator */}
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
            <div className="w-1.5 h-3 bg-gold-base rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* ─── 2. OUR STORY ─── */}
      <section className="relative py-24 md:py-32 bg-primary">
        {/* faint decorative ring */}
        <div className="absolute -left-40 top-20 w-80 h-80 rounded-full border border-gold-base/10 pointer-events-none" />

        <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* text */}
            <FadeIn direction="left">
              <p className="text-gold-base text-xs font-bold tracking-[0.3em] uppercase mb-4">Our Story</p>
              <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight leading-tight mb-8">
                Founded in <span className="text-gold-base">Johannesburg</span>
              </h2>

              <div className="space-y-6 text-white/70 font-light leading-relaxed text-base md:text-lg">
                <p>
                  Skiva Africa Tours emerged from a simple vision: to elevate the standard of passenger transport in South Africa from merely functional to truly exceptional.
                </p>
                <p>
                  Today, our fleet serves discerning corporate executives, international tourists, and private groups who demand reliability, comfort, and security above all else. Explore our full range of <Link to="/services" className="text-gold-base hover:underline font-medium">transport services</Link> tailored for every journey.
                </p>
                <p className="text-white/90 font-medium italic border-l-4 border-gold-base pl-6">
                  "We don't just drive; we host. Every chauffeur is trained not only in advanced driving techniques but in executive hospitality, ensuring your journey is as refined as your destination."
                </p>
              </div>
            </FadeIn>

            {/* image */}
            <FadeIn direction="right" delay={0.2}>
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-tr from-gold-base/20 to-transparent rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative overflow-hidden rounded-sm">
                  <img
                    src="/gallery/Our Founder.jpg"
                    alt="Founder of Skiva Africa Tours"
                    className="w-full h-[400px] md:h-[500px] object-cover transform group-hover:scale-105 transition-transform duration-700 object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent" />
                </div>
                {/* floating badge */}
                <div className="absolute -bottom-6 -left-6 bg-gold-base text-black px-6 py-4 rounded-sm shadow-2xl">
                  <p className="text-3xl font-black">100%</p>
                  <p className="text-xs font-bold uppercase tracking-widest">Commitment</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── 3. FLEET OPERATIONS ─── */}
      <section className="relative py-24 md:py-32 bg-[#0a0a0a]">
        {/* diagonal gold accent */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-gold-base/10 to-transparent clip-triangle pointer-events-none" />

        <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
          <FadeIn className="text-center mb-16">
            <p className="text-gold-base text-xs font-bold tracking-[0.3em] uppercase mb-4">Transportation Options</p>
            <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight">
              Fleet <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light to-gold-base">Operations</span>
            </h2>
            <div className="w-24 h-1 bg-gold-base mx-auto mt-8" />
            <p className="mt-6 text-white/60 font-light max-w-xl mx-auto">
              We offer various transportation options to suit every occasion and group size. View our <Link to="/services" className="text-gold-base hover:underline font-medium">transport services</Link> or <Link to="/contact" className="text-gold-base hover:underline font-medium">contact Skiva Africa Tours</Link> for custom logistics planning.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* fleet video */}
            <FadeIn direction="left">
              <div className="relative overflow-hidden rounded-sm group shadow-2xl">
                <video
                  src="/gallery/Fleet%20Operations.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-[350px] md:h-[450px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent pointer-events-none" />
                <div className="absolute inset-0 border border-white/10 rounded-sm pointer-events-none group-hover:border-gold-base/30 transition-colors duration-500" />
              </div>
            </FadeIn>

            {/* fleet cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {fleetItems.map((item, i) => (
                <FadeIn key={item.seats} delay={i * 0.12} direction="up">
                  <motion.div
                    whileHover={{ y: -6, scale: 1.02 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    className="relative p-6 bg-[#141414] border border-white/5 rounded-sm group hover:border-gold-base/30 transition-colors duration-500 cursor-default"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-gold-base/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-sm" />
                    <div className="relative z-10">
                      <div className="w-14 h-14 rounded-full bg-gold-base/10 flex items-center justify-center text-gold-base mb-4 group-hover:bg-gold-base/20 transition-colors">
                        <item.icon className="w-7 h-7" />
                      </div>
                      <h3 className="text-xl font-bold text-white uppercase tracking-wider mb-2">{item.seats}</h3>
                      <p className="text-white/50 text-sm font-light leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── 4. VISION & MISSION (split layout) ─── */}
      <section className="relative py-24 md:py-32 bg-primary overflow-hidden">
        {/* background pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #d4af37 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

        <div className="container mx-auto px-4 lg:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-0">

            {/* VISION */}
            <FadeIn direction="left" className="relative">
              <div className="p-8 md:p-12 lg:p-16 bg-[#161616] border border-white/5 rounded-t-sm lg:rounded-l-sm lg:rounded-tr-none h-full">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold-base/20 to-gold-base/5 flex items-center justify-center text-gold-base">
                    <Eye className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-gold-base text-xs font-bold tracking-[0.3em] uppercase">Our Purpose</p>
                    <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">Vision</h2>
                  </div>
                </div>

                <p className="text-white/70 font-light leading-relaxed mb-8">
                  To establish ourselves as one of the best logistics service providers by gearing the respect of our customers and developing successful and satisfied customers and employees by focusing on our function.
                </p>

                <p className="text-white/50 text-sm font-light mb-4">In support of this we are committed to:</p>
                <ul className="space-y-3">
                  {[
                    'A culture of leadership and innovation.',
                    'A client focused delivery culture.',
                    'Fostering long-term business relationships based on trust and integrity.',
                  ].map((item, i) => (
                    <FadeIn key={i} delay={0.1 * i} direction="none">
                      <li className="flex items-start gap-3 text-white/60 font-light text-sm">
                        <CheckCircle2 className="w-5 h-5 text-gold-base mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>

                <div className="mt-8 p-4 bg-gold-base/5 border-l-4 border-gold-base rounded-r-sm">
                  <p className="text-white/60 text-sm font-light leading-relaxed italic">
                    These principles form part of our core values, and it is our intention to ensure that they are cultivated, entrenched and are made visible wherever Skiva Africa Tours is operating.
                  </p>
                </div>
              </div>
            </FadeIn>

            {/* MISSION */}
            <FadeIn direction="right" delay={0.15} className="relative">
              <div className="p-8 md:p-12 lg:p-16 bg-[#1a1a1a] border border-white/5 border-t-0 lg:border-t lg:border-l-0 rounded-b-sm lg:rounded-r-sm lg:rounded-bl-none h-full">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold-base/20 to-gold-base/5 flex items-center justify-center text-gold-base">
                    <Target className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-gold-base text-xs font-bold tracking-[0.3em] uppercase">Our Drive</p>
                    <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">Mission</h2>
                  </div>
                </div>

                <p className="text-white/70 font-light leading-relaxed mb-10">
                  As a third-party logistics company our mission is being passionate, dedicated and accountable, we will provide excellent service that would be recognised by stakeholders.
                </p>

                <h3 className="text-sm font-bold text-white uppercase tracking-[0.2em] mb-6">Values Underpinning Our Mission</h3>

                <div className="space-y-4">
                  {values.map((v, i) => (
                    <FadeIn key={v.label} delay={0.08 * i} direction="none">
                      <motion.div
                        whileHover={{ x: 8 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                        className="flex items-center gap-4 p-3 rounded-sm bg-gradient-to-r from-white/[0.03] to-transparent hover:from-white/[0.06] transition-colors cursor-default"
                      >
                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${v.color} flex items-center justify-center flex-shrink-0`}>
                          <v.icon className="w-5 h-5 text-gold-base" />
                        </div>
                        <span className="text-white/80 font-medium text-sm tracking-wide">{v.label}</span>
                      </motion.div>
                    </FadeIn>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── 5. VEHICLE OPERATIONS ─── */}
      <section className="relative py-24 md:py-32 bg-[#0a0a0a] overflow-hidden">
        {/* decorative lines */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-base/20 to-transparent" />

        <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

            {/* content */}
            <FadeIn direction="left">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-6 h-6 text-gold-base" />
                <p className="text-gold-base text-xs font-bold tracking-[0.3em] uppercase">Safety & Compliance</p>
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight mb-4">
                Vehicle <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light to-gold-base">Operations</span>
              </h2>
              <div className="w-16 h-1 bg-gold-base mb-8" />

              <div className="space-y-5">
                {vehicleOps.map((item, i) => (
                  <FadeIn key={i} delay={0.08 * i} direction="none">
                    <motion.div
                      whileHover={{ x: 6 }}
                      transition={{ type: 'spring', stiffness: 400 }}
                      className="flex items-start gap-4 group cursor-default"
                    >
                      <div className="mt-1 w-8 h-8 rounded-full bg-gold-base/10 flex items-center justify-center flex-shrink-0 group-hover:bg-gold-base/20 transition-colors">
                        <CheckCircle2 className="w-4 h-4 text-gold-base" />
                      </div>
                      <p className="text-white/70 font-light leading-relaxed group-hover:text-white/90 transition-colors">
                        {item}
                      </p>
                    </motion.div>
                  </FadeIn>
                ))}
              </div>
            </FadeIn>

            {/* stats & highlights */}
            <FadeIn direction="right" delay={0.2}>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: 'R500K', label: 'Liability Cover', sub: 'Per Vehicle', icon: Shield },
                  { value: '24/7', label: 'Vehicle Monitoring', sub: 'Real-time Tracking', icon: MapPin },
                  { value: '100%', label: 'Maintenance', sub: 'Compliance Rate', icon: Wrench },
                  { value: '9+', label: 'Provinces', sub: 'Country-wide', icon: TrendingUp },
                ].map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.15 * i, duration: 0.6 }}
                    whileHover={{ y: -4 }}
                    className="p-6 bg-[#141414] border border-white/5 rounded-sm text-center group hover:border-gold-base/20 transition-all duration-500"
                  >
                    <div className="w-12 h-12 rounded-full bg-gold-base/10 flex items-center justify-center text-gold-base mx-auto mb-4 group-hover:bg-gold-base/20 transition-colors">
                      <stat.icon className="w-6 h-6" />
                    </div>
                    <p className="text-2xl md:text-3xl font-black text-gold-base">{stat.value}</p>
                    <p className="text-white/80 text-xs font-bold uppercase tracking-widest mt-1">{stat.label}</p>
                    <p className="text-white/40 text-xs font-light mt-1">{stat.sub}</p>
                  </motion.div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── 6. INTERNAL PROCEDURES ─── */}
      <section className="relative py-24 md:py-32 bg-primary overflow-hidden">
        {/* gold corner accent */}
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-gold-base/5 to-transparent pointer-events-none" />

        <div className="container mx-auto px-4 lg:px-8 max-w-5xl relative z-10">
          <FadeIn className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gold-base/10 px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-gold-base" />
              <span className="text-gold-base text-xs font-bold tracking-[0.2em] uppercase">Our Standards</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight">
              Internal <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light to-gold-base">Procedures</span>
            </h2>
            <p className="mt-6 text-white/60 font-light max-w-2xl mx-auto">
              The following internal procedures are in place to ensure that the company delivers on its promises.
            </p>
          </FadeIn>

          <div className="relative">
            {/* timeline line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-gold-base/40 via-gold-base/20 to-transparent" />

            {procedures.map((item, i) => {
              const isLeft = i % 2 === 0;
              return (
                <FadeIn key={i} delay={i * 0.1} direction={isLeft ? 'left' : 'right'}>
                  <div className={`relative flex items-start mb-12 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    {/* dot */}
                    <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10">
                      <motion.div
                        whileInView={{ scale: [0, 1.2, 1] }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.1 }}
                        className="w-4 h-4 rounded-full bg-gold-base shadow-[0_0_20px_rgba(212,175,55,0.4)]"
                      />
                    </div>

                    {/* card */}
                    <div className={`ml-14 md:ml-0 md:w-[calc(50%-2rem)] ${isLeft ? 'md:pr-8 md:text-right' : 'md:pl-8 md:text-left'}`}>
                      <motion.div
                        whileHover={{ y: -4 }}
                        className="p-6 bg-[#161616] border border-white/5 rounded-sm hover:border-gold-base/20 transition-all duration-500"
                      >
                        <div className={`flex items-center gap-3 mb-3 ${isLeft ? 'md:justify-end' : 'md:justify-start'}`}>
                          <Clock className="w-5 h-5 text-gold-base" />
                          <span className="text-gold-base text-xs font-bold uppercase tracking-widest">Step {i + 1}</span>
                        </div>
                        <p className="text-white/80 font-light leading-relaxed">{item}</p>
                      </motion.div>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── 7. CTA BANNER ─── */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold-base to-[#b8960d]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/hero.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }} />

        <div className="container mx-auto px-4 relative z-10 text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-5xl font-black text-black uppercase tracking-tight mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-black/70 font-light text-lg max-w-xl mx-auto mb-10">
              Book your premium transport today and discover why South Africa's most discerning clients trust Skiva Africa Tours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book"
                className="px-10 py-4 bg-black text-gold-base font-bold uppercase tracking-widest text-sm hover:bg-[#1a1a1a] transition-colors rounded-sm flex items-center justify-center gap-2 group"
              >
                Book Now
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="px-10 py-4 bg-transparent border-2 border-black text-black font-bold uppercase tracking-widest text-sm hover:bg-black/10 transition-colors rounded-sm"
              >
                Contact Skiva Africa Tours
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
